import { CompletionContext } from '@codemirror/autocomplete';
import { EditorState } from '@codemirror/state';

import { verifyQuery } from './dql-linter.js';

function isStartOfCommandOrParameter(context: CompletionContext) {
  return context.matchBefore(/[,|]\s+$/);
}

export async function isInvalidAtEnd(query: string) {
  const trimmedQuery = query.trimEnd();
  const { valid = true, notifications = [] } =
    (await verifyQuery(trimmedQuery)) ?? {};

  const hasErrorNotificationAtEnd = notifications?.some(
    (notification) =>
      notification.severity === 'ERROR' &&
      notification.syntaxPosition?.end.index === trimmedQuery.length - 1,
  );

  return !valid && hasErrorNotificationAtEnd;
}

/**
 * Function that detemines if the autocomplete dropdown should be opened
 *
 * @param update - Information on the current changes from the update listener
 * @param state - EditorState
 * @returns true when the autocomplete should be opened
 */
export async function openAutocomplete(
  update: {
    isTyping: boolean;
    isDeleting: boolean;
    isFocused: boolean;
    selectionChanged: boolean;
  },
  state: EditorState,
): Promise<boolean> {
  const { isTyping, isDeleting, isFocused, selectionChanged } = update;

  const context = new CompletionContext(state, state.selection.main.to, false);

  // open autocomplete when content is empty
  // or cursor is at the very end following a comma or pipe
  if (selectionChanged || isFocused) {
    const fullContent = state.doc.toString();
    const cursorAtTheEnd = fullContent.length === state.selection.ranges[0].to;

    if (
      fullContent.trim() === '' ||
      (cursorAtTheEnd && isStartOfCommandOrParameter(context))
    ) {
      return true;
    }
  }

  const hasWhitespaceBefore = context.matchBefore(/\s/);

  // open autocomplete when the user is typing following a command (==keyword)
  if (
    (isTyping || isDeleting) &&
    context.tokenBefore(['keyword']) &&
    hasWhitespaceBefore
  ) {
    return true;
  }

  // open autocomplete if the user is typing following a comma or pipe
  if (isTyping && isStartOfCommandOrParameter(context)) {
    return true;
  }

  // open autocomplete if query is invalid and a space was typed beforehand
  if (isTyping && hasWhitespaceBefore) {
    const query = state.doc.slice(0, state.selection.main.to).toString();
    if (await isInvalidAtEnd(query)) {
      return true;
    }
  }

  const characterAfter = state.doc
    .slice(state.selection.main.to, state.selection.main.to + 1)
    .toString();
  const hasSpaceOrEOFAfter = characterAfter.trim().length === 0;

  // open autocomplete if
  //  - a whitespace was deleted
  //  - the last char was deleted
  if (isDeleting && hasSpaceOrEOFAfter) {
    return true;
  }

  return false;
}
